import { forwardRef } from "react";
import { clsx } from "clsx";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'outline' | 'subtle' | 'destructive' | 'secondary' | 'ghost' | 'default';
  size?: 'xs' | 'sm' | 'default' | 'lg' | 'xl';
  loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'default', loading = false, children, disabled, ...props }, ref) => {
    return (
      <button
        className={clsx(
          // Base styles
          "inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none relative overflow-hidden",
          // Size variants with consistent dimensions
          {
            "h-7 px-2 text-xs": size === 'xs',
            "h-8 px-3 text-xs": size === 'sm', 
            "h-9 px-4 text-sm": size === 'default',
            "h-10 px-6 text-sm": size === 'lg',
            "h-11 px-8 text-base": size === 'xl',
          },
          // Color variants using design system
          {
            "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm focus:ring-primary/40": variant === 'primary',
            "border border-border bg-background text-foreground hover:bg-muted hover:border-border/80 focus:ring-primary/40": variant === 'outline',
            "bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground focus:ring-primary/40": variant === 'subtle' || variant === 'secondary',
            "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm focus:ring-destructive/40": variant === 'destructive',
            "text-foreground hover:bg-muted/50 focus:ring-primary/40": variant === 'ghost',
            "bg-card text-card-foreground border border-border hover:bg-muted focus:ring-primary/40": variant === 'default',
          },
          className
        )}
        disabled={disabled || loading}
        ref={ref}
        {...props}
      >
        {loading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button };